import React, { useState } from 'react';
import { Languages, BookOpen, PlayCircle, ArrowRight, Sun, Sunrise, Moon, Flame } from 'lucide-react';
import { useTranslation } from '../i18n';
import { growthApi } from '../screens/courses/growthApi';

function GrowthPlanCard({ meta: initialMeta, postId }) {
  const { t, lang } = useTranslation('courses');
  const [meta, setMeta] = useState(initialMeta);
  const [showOriginal, setShowOriginal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateErr, setTranslateErr] = useState(false);

  const sourceLang = meta.sourceLang || 'en';
  const viewerLang = lang === 'es' ? 'es' : 'en';
  const isCrossLang = sourceLang !== viewerLang;

  const hasCachedTranslation = !!(meta.qaAlt || meta.unitTitleAlt);
  const showAlt = isCrossLang && hasCachedTranslation && !showOriginal;

  const displayUnitTitle = showAlt && meta.unitTitleAlt ? meta.unitTitleAlt : meta.unitTitle;
  const displayQa = showAlt && Array.isArray(meta.qaAlt) && meta.qaAlt.length === (meta.qa?.length || 0)
    ? meta.qaAlt
    : meta.qa;

  async function handleTranslate() {
    if (!postId || translating) return;
    setTranslating(true);
    setTranslateErr(false);
    try {
      const d = await growthApi.translateSharedPost(postId);
      if (d?.meta) {
        setMeta(d.meta);
        setShowOriginal(false);
      }
    } catch {
      setTranslateErr(true);
    } finally {
      setTranslating(false);
    }
  }

  // Show toggle when source differs from viewer's language. If we already have
  // cached *Alt fields, toggle locally; otherwise the click triggers a lazy
  // backfill against the API (mirrors the Prayer Wall pattern).
  const canToggle = isCrossLang && (hasCachedTranslation || !!postId);

  return (
    <div className="growth-share-card">
      <div className="growth-share-header">
        <span className="growth-share-badge">Grow Deeper</span>
        <span className="growth-share-plan">{meta.planTitle}</span>
      </div>
      <div className="growth-share-unit">
        {meta.unitLabel} {meta.unitNum}{displayUnitTitle ? ` · ${displayUnitTitle}` : ''}
      </div>
      {meta.verses && (
        <div className="growth-share-verse">{meta.verses}</div>
      )}
      {displayQa?.filter(item => item.a).length > 0 && (
        <div className="growth-share-qa">
          {displayQa.filter(item => item.a).map((item, i) => (
            <div key={i} className="growth-share-qa-item">
              {item.q && <p className="growth-share-question">{item.q}</p>}
              <p className="growth-share-answer">{item.a}</p>
            </div>
          ))}
        </div>
      )}
      {canToggle && (
        <div className="growth-share-translate-row">
          <button
            type="button"
            className="growth-share-translate-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasCachedTranslation && !translating) {
                handleTranslate();
              } else {
                setShowOriginal(v => !v);
              }
            }}
            disabled={translating}
          >
            <Languages size={11}/>
            {translating
              ? t('translating')
              : translateErr
                ? t('translateRetry')
                : showAlt
                  ? t('showOriginal')
                  : t('viewTranslation')}
          </button>
        </div>
      )}
    </div>
  );
}

export { GrowthPlanCard };

function WellPublishedCard({ meta, heroUrl }) {
  const hasHero      = !!heroUrl;
  const hasScripture = Array.isArray(meta.scriptures) && meta.scriptures.length > 0;
  const hasNotes     = !!meta.notesPreview;
  const subline      = [meta.seriesTitle, meta.weekLabel, meta.speaker].filter(Boolean).join(' · ');

  // The whole card navigates to the thread when we have a threadId. Use an
  // anchor (not an onClick div) so middle-click / open-in-new-tab work and to
  // match the inner CTA pattern. Fall back to a plain div when there's no
  // thread to open.
  const Tag       = meta.threadId ? 'a' : 'div';
  const tagProps  = meta.threadId ? { href: `/well/thread/${meta.threadId}` } : {};

  return (
    <Tag {...tagProps} className={`well-share-card${hasHero ? ' well-share-card--hero' : ''}`}>
      <div className="well-share-card__media">
        {hasHero
          ? <img src={heroUrl} alt="" className="well-share-card__photo" loading="lazy"/>
          : <div className="well-share-card__gradient" aria-hidden="true"/>}
        <div className="well-share-card__overlay">
          <span className="well-share-card__badge">
            <Sun size={12} strokeWidth={2.5}/>
            <span>The Well is open</span>
          </span>
          {meta.hasVideo && (
            <span className="well-share-card__video-pill" title="Sermon video available">
              <PlayCircle size={12} strokeWidth={2.25}/>
              <span>Video</span>
            </span>
          )}
        </div>
      </div>

      <div className="well-share-card__body">
        {meta.seriesTitle && (
          <div className="well-share-card__eyebrow">{meta.seriesTitle}</div>
        )}
        <h3 className="well-share-card__title">{meta.title}</h3>
        {subline && !meta.seriesTitle ? (
          <div className="well-share-card__subline">{subline}</div>
        ) : (meta.weekLabel || meta.speaker) ? (
          <div className="well-share-card__subline">
            {[meta.weekLabel, meta.speaker].filter(Boolean).join(' · ')}
          </div>
        ) : null}

        {hasScripture && (
          <div className="well-share-card__scriptures">
            {meta.scriptures.map((ref, i) => (
              <span key={i} className="well-share-card__scripture-chip">
                <BookOpen size={11} strokeWidth={2.25}/>
                <span>{ref}</span>
              </span>
            ))}
          </div>
        )}

        {hasNotes && (
          <p className="well-share-card__notes">{meta.notesPreview}</p>
        )}

        {meta.threadId && (
          <span className="well-share-card__cta">
            <span>Join the Discussion</span>
            <ArrowRight size={14} strokeWidth={2.25}/>
          </span>
        )}
      </div>
    </Tag>
  );
}


/**
 * A Daily Rhythm half opened in the community.
 *
 * Morning and evening are one card with two faces rather than two components:
 * the feed should read as one rhythm running through the week, not as two
 * features taking turns. What differs is only what a member is being called to
 * — a passage to read, or a story to watch — so that is what each face leads
 * with, and the sunrise/moon + orange/teal pairing is the same one the day
 * screen uses for .dr-half--morning / .dr-half--evening.
 *
 * Deliberately flat and quiet. This fires twice a day to everyone in the
 * community, so a card with a hero image the size of The Well's — which fires
 * once a week — would take over the feed by Wednesday.
 */
/** "Mon, Sep 28" for a date key, in the reader's language. Formatted in UTC —
 *  the key is UTC-midnight, and any other zone slides it a day for someone
 *  west of it (the formatDayLabel rule). */
function rhythmCardDate(dateKey, lang) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(lang || undefined, {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    }).format(d);
  } catch { return dateKey; }
}

// [FASTING_UPDATE] — a relay hand-off, a together fast's day beginning, or the
// end of a fast (lib/autoPost.js postFastingUpdate). Minted by @ignite past the
// slot's atomic claim, so it appears once. Names are the snapshot's first names;
// the sentence is built here so it reads in the viewer's language.
function FastingUpdateCard({ meta, groupId }) {
  const { t, lang } = useTranslation('fasting');
  const community = groupId || meta.communityId;
  const href = community && meta.programId ? `/communities/${community}/fasting/${meta.programId}` : null;
  const Tag = href ? 'a' : 'div';
  const join = (names) => {
    const list = (names || []).filter(Boolean);
    if (list.length <= 1) return list[0] || '';
    if (list.length <= 3) return `${list.slice(0, -1).join(', ')} ${t('feedCard.and')} ${list[list.length - 1]}`;
    return `${list.slice(0, 3).join(', ')} ${t('feedCard.and')} ${t('feedCard.others', list.length - 3)}`;
  };
  const prev = join(meta.prevNames);
  const next = join(meta.nextNames);
  const n = meta.nextCount ?? (meta.nextNames || []).length;

  let body;
  if (meta.kind === 'complete')   body = t('feedCard.complete', meta.people || 0, meta.shifts || 0);
  else if (meta.kind === 'day')   body = n ? t('feedCard.day', n) : t('feedCard.dayNone');
  else if (!next)                 body = t('feedCard.handoffOpen', prev);
  else if (!prev)                 body = t('feedCard.handoffStart', next, n);
  else                            body = t('feedCard.handoff', prev, next, n);

  let when = '';
  if (meta.startsAt && meta.endsAt && meta.kind !== 'complete') {
    try {
      const fmt = (iso, withDay) => new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', {
        timeZone: meta.timezone || undefined,
        ...(withDay ? { weekday: 'short', month: 'short', day: 'numeric' } : {}),
        hour: 'numeric', minute: '2-digit',
      }).format(new Date(iso));
      when = `${fmt(meta.startsAt, true)} – ${fmt(meta.endsAt, false)}`;
    } catch { /* an unknown zone just drops the line */ }
  }

  return (
    <Tag {...(href ? { href } : {})} className="fst-feed-card">
      <div className="fst-feed-card__eyebrow"><Flame size={13} strokeWidth={2.5} /> {t('feedCard.badge')}</div>
      {meta.programTitle && <p className="fst-feed-card__title">{meta.programTitle}</p>}
      <p className="fst-feed-card__body">{body}</p>
      {when && <p className="fst-feed-card__when">{when}</p>}
      {href && <span className="fst-feed-card__cta">{t('feedCard.cta')} →</span>}
    </Tag>
  );
}

function DailyRhythmPublishedCard({ meta, groupId }) {
  const { t, lang } = useTranslation('rhythm');
  const morning = meta.slot === 'morning';
  const community = groupId || meta.communityId;
  const href    = community && meta.programId && meta.date
    ? `/communities/${community}/rhythm/${meta.programId}/day/${meta.date}?src=feed`
    : null;
  const Tag      = href ? 'a' : 'div';
  const tagProps = href ? { href } : {};

  // Where in the week this is: program · Day N · Mon, Sep 28.
  const eyebrow = [
    meta.programTitle,
    // != null, not truthiness: a program can start at Day 0.
    meta.dayNumber != null ? t('dayN', meta.dayNumber) : '',
    rhythmCardDate(meta.date, lang),
  ].filter(Boolean).join(' · ');

  // The theme names the day ("God, Speak to Me"); a bare reference or story
  // title reads as a citation, not a day, so those move below it. Older cards
  // (minted before keyVerse existed) simply skip the quote.
  const title = meta.theme
             || (morning ? meta.scriptureRef : meta.storyTitle)
             || t('dayN', meta.dayNumber);
  // IGNITE Week's story titles equal the day's focus; saying it twice is noise.
  const story = !morning && meta.storyTitle && meta.storyTitle !== meta.focus
             && meta.storyTitle !== title ? meta.storyTitle : '';
  const refChip = morning && meta.scriptureRef && meta.scriptureRef !== title ? meta.scriptureRef : '';
  const focus   = meta.focus && meta.focus !== title ? meta.focus : '';
  const cta = morning ? t('feedCard.startToday')
            : meta.hasVideo ? t('feedCard.watchStory')
            : t('feedCard.openEvening');

  return (
    <Tag {...tagProps} className={`dr-share-card dr-share-card--${morning ? 'morning' : 'evening'}`}>
      {eyebrow && <div className="dr-share-card__eyebrow">{eyebrow}</div>}

      <div className="dr-share-card__head">
        <span className="dr-share-card__badge">
          {morning ? <Sunrise size={13} strokeWidth={2.5} /> : <Moon size={13} strokeWidth={2.5} />}
          <span>{morning ? t('morning') : t('evening')}</span>
        </span>
        {meta.hasVideo && (
          <span className="dr-share-card__video-pill">
            <PlayCircle size={12} strokeWidth={2.25} />
            <span>{t('feedCard.video')}</span>
          </span>
        )}
      </div>

      <h3 className="dr-share-card__title">{title}</h3>

      {morning && meta.keyVerse && (
        <blockquote className="dr-share-card__verse">
          <p>“{meta.keyVerse}”</p>
          {meta.keyVerseRef && <cite>— {meta.keyVerseRef}</cite>}
        </blockquote>
      )}

      {story && <div className="dr-share-card__sub">{t('feedCard.tonightsStory', story)}</div>}

      {(refChip || focus) && (
        <div className="dr-share-card__chips">
          {refChip && (
            <span className="dr-share-card__chip">
              <BookOpen size={11} strokeWidth={2.25} />
              <span>{refChip}</span>
            </span>
          )}
          {focus && <span className="dr-share-card__chip dr-share-card__chip--focus">{focus}</span>}
        </div>
      )}

      {href && (
        <span className="dr-share-card__cta">
          <span>{cta}</span>
          <ArrowRight size={14} strokeWidth={2.25} />
        </span>
      )}
    </Tag>
  );
}

export function renderShareCard(text, { groupId, postId, mediaUrl } = {}) {
  if (!text) return null;

  if (text.startsWith('[GROWTH_INVITE]')) {
    try {
      const meta = JSON.parse(text.slice(15));
      return (
        <div className="growth-share-card" style={{borderLeftColor: 'var(--teal)'}}>
          <div className="growth-share-header">
            <span className="growth-share-badge" style={{background: 'var(--teal)', color:'#fff'}}>🌱 Growth Plan</span>
          </div>
          <p style={{fontSize:14,margin:'4px 0 8px',color:'var(--text)',padding:'0 14px'}}>
            Started <strong>{meta.planTitle}</strong> — join them on this journey!
          </p>
          <a href={`/grow/${meta.planId}`} className="btn btn-outline" style={{fontSize:12,padding:'5px 12px',display:'inline-flex',gap:4,margin:'0 14px 10px'}}>
            View Plan →
          </a>
        </div>
      );
    } catch {}
  }

  if (text.startsWith('[COMMUNITY_JOIN]')) {
    try {
      const meta = JSON.parse(text.slice(16));
      return (
        <div className="growth-share-card" style={{borderLeftColor: 'var(--teal)'}}>
          <div className="growth-share-header">
            <span className="growth-share-badge" style={{background: 'var(--teal)', color:'#fff'}}>🔥 Joined</span>
          </div>
          <p style={{fontSize:14,margin:'4px 0 10px',color:'var(--text)',padding:'0 14px'}}>
            Just joined <strong>{meta.communityName || 'the community'}</strong> — say hi!
          </p>
          {meta.communityId && (
            <a href={`/communities/${meta.communityId}`} className="btn btn-outline" style={{fontSize:12,padding:'5px 12px',display:'inline-flex',gap:4,margin:'0 14px 10px'}}>
              View Community →
            </a>
          )}
        </div>
      );
    } catch {}
  }

  if (text.startsWith('[BRP_PUBLISHED]')) {
    try {
      const meta = JSON.parse(text.slice(15));
      const accent = meta.planColor || '#0d9488';
      const unitWord = meta.cadence === 'week' ? 'weeks' : 'days';
      return (
        <div className="growth-share-card" style={{borderLeftColor: accent}}>
          <div className="growth-share-header">
            <span className="growth-share-badge" style={{background: accent, color:'#fff'}}>📖 New Bible Reading Plan</span>
          </div>
          <p style={{fontSize:15,fontWeight:600,margin:'4px 0 4px',color:'var(--text)',padding:'0 14px'}}>
            {meta.planTitle}
          </p>
          {meta.totalDays && (
            <p style={{fontSize:12,margin:'0 0 6px',color:'var(--text-lt)',padding:'0 14px'}}>
              {meta.totalDays} {unitWord}
            </p>
          )}
          {meta.description && (
            <p style={{fontSize:13,margin:'4px 0 10px',color:'var(--text)',padding:'0 14px',lineHeight:1.45}}>
              {meta.description}
            </p>
          )}
          {groupId && (
            <a href={`/communities/${groupId}/bible`} className="btn btn-outline" style={{fontSize:12,padding:'5px 12px',display:'inline-flex',gap:4,margin:'0 14px 10px'}}>
              Open Plan →
            </a>
          )}
        </div>
      );
    } catch {}
  }

  if (text.startsWith('[FASTING_UPDATE]')) {
    try {
      const meta = JSON.parse(text.slice('[FASTING_UPDATE]'.length));
      return <FastingUpdateCard meta={meta} groupId={groupId || meta.communityId} />;
    } catch {}
  }

  if (text.startsWith('[DR_PUBLISHED]')) {
    try {
      const meta = JSON.parse(text.slice(14));
      return <DailyRhythmPublishedCard meta={meta} groupId={groupId || meta.communityId} />;
    } catch {}
  }

  if (text.startsWith('[WELL_PUBLISHED]')) {
    try {
      const meta = JSON.parse(text.slice(16));
      return <WellPublishedCard meta={meta} heroUrl={mediaUrl}/>;
    } catch {}
  }

  if (text.startsWith('[WELL_DISCUSSION]')) {
    try {
      const meta = JSON.parse(text.slice(17));
      // The whole card navigates to the thread when we have a threadId. Wrap in
      // an anchor (not the shared .growth-share-card, whose class is reused by
      // other card types) so the entire card is clickable. Avoid nesting an
      // anchor inside an anchor — the inner CTA becomes a plain span.
      const Tag      = meta.threadId ? 'a' : 'div';
      const tagProps = meta.threadId
        ? { href: `/well/thread/${meta.threadId}`, className: 'well-discussion-share-link' }
        : {};
      return (
        <Tag {...tagProps}>
          <div className="growth-share-card" style={{borderLeftColor: '#F59E0B'}}>
            <div className="growth-share-header">
              <span className="growth-share-badge" style={{background: '#F59E0B', color:'#fff'}}>💬 New Well discussion</span>
            </div>
            <p style={{fontSize:15,fontWeight:600,margin:'4px 0 4px',color:'var(--text)',padding:'0 14px'}}>
              {meta.threadTitle}
            </p>
            {meta.seriesTitle && (
              <p style={{fontSize:12,margin:'0 0 6px',color:'var(--text-lt)',padding:'0 14px'}}>
                {meta.seriesTitle}
              </p>
            )}
            {meta.preview && (
              <p style={{fontSize:13,margin:'4px 0 10px',color:'var(--text)',padding:'0 14px',lineHeight:1.45,fontStyle:'italic'}}>
                “{meta.preview}”
              </p>
            )}
            {meta.threadId && (
              <span className="btn btn-outline" style={{fontSize:12,padding:'5px 12px',display:'inline-flex',gap:4,margin:'0 14px 10px'}}>
                Open Thread →
              </span>
            )}
          </div>
        </Tag>
      );
    } catch {}
  }

  if (text.startsWith('[GROWTH]')) {
    try {
      const meta = JSON.parse(text.slice(8));
      return <GrowthPlanCard meta={meta} postId={postId}/>;
    } catch {}
  }

  return null;
}
